// Sponsor adapters — wire payload -> view model.
//
// The sponsor API already speaks camelCase, so there is no key renaming to do here
// (Hard Rule #2's mapper still owns the boundary: this is where DERIVED display values
// are computed, once, so components stay presentational). What actually needs deriving:
//
//   - the hero balance split into dollars + cents, because the design renders them at
//     two different type sizes
//   - the days until the next payroll, computed from `nextDue` at render time rather
//     than stored, or a fixture goes stale (the WORDING lives in PayrollCard — an
//     adapter has no `t`, so a sentence built here would be permanently English)
//   - the holdings top/tail split behind the "+ N more" disclosure
//   - percentage strings, including the "<0.01%" floor for a holding too small to round
//
// Reconciliation is guaranteed upstream (the generator asserts it), so nothing here
// re-derives a total from its parts — doing so would silently paper over a contract bug.
import { mapFundDetails } from '../../../utils/fundDetail';
import { FUND_MIX, colorAt } from '../../../utils/chartColors';

const DISCLOSED_HOLDINGS = 6; // rows visible before the "+ N more holdings" disclosure
const BAR_LABEL_MIN_PCT = 8; // narrower than this band and the in-bar percentage won't fit

/** Split a currency amount into its integer part and cents, both as display strings. */
export function splitAmount(value) {
  const safe = Number.isFinite(value) ? value : 0;
  const negative = safe < 0;
  const abs = Math.abs(safe);
  const whole = Math.floor(abs);
  const cents = Math.round((abs - whole) * 100);
  return {
    dollars: `${negative ? '-' : ''}$${whole.toLocaleString('en-US')}`,
    cents: `.${String(cents).padStart(2, '0')}`,
  };
}

/**
 * Percentage as a display string, trimming a trailing ".0" so 100.0 reads "100%" while
 * 62.5 stays "62.5%". A non-zero value that rounds away becomes "<0.01%" rather than
 * "0%", so a real holding never looks like nothing.
 */
export function formatPct(pct, { digits = 1, floor = null } = {}) {
  if (!Number.isFinite(pct)) return '';
  if (floor != null && pct > 0 && pct < floor) return `<${floor}%`;
  const fixed = pct.toFixed(digits);
  return `${fixed.replace(/\.0+$/, '')}%`;
}

/**
 * Whole days from today until an ISO date, or null when there is no date.
 * Compared at UTC midnight so a late-evening local time can't read as a day early.
 */
export function daysUntil(iso, today = new Date()) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const target = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - now) / 86400000);
}

// Distinct colours for the plan-mix bar, so two plans are told apart at a glance. Long
// enough for a realistic sponsor; anything beyond wraps round the ramp.
const PLAN_MIX_COLORS = ['#002266', '#0077CC', '#4DA3E0', '#82BEEA', '#B4CEE8', '#552299'];

/** One plan card on the sponsor landing plan-picker. */
function mapPlanCard(card, index, totalBalance) {
  const pct = totalBalance ? (card.balance / totalBalance) * 100 : 0;
  return {
    ...card,
    // The design shows a single meta line; a plan with no employer falls back to its
    // descriptor rather than leaving a dangling separator.
    subtitle: card.company || card.descriptor || null,
    balanceLabel: splitAmount(card.balance),
    // Share of the assets this sponsor administers — drives the plan-mix bar.
    color: PLAN_MIX_COLORS[index % PLAN_MIX_COLORS.length],
    allocationPct: pct,
    allocationPctLabel: formatPct(pct, { digits: 1, floor: 0.1 }),
  };
}

export function mapSponsorOverviewResponse(api) {
  if (!api) return null;
  const { sponsor, plans = [] } = api;
  const totalBalance = plans.reduce((sum, p) => sum + (p.balance || 0), 0);

  return {
    sponsor: {
      ...sponsor,
      fullName: [sponsor.firstName, sponsor.lastName].filter(Boolean).join(' '),
      initials: `${sponsor.firstName || ''}`.slice(0, 2).toUpperCase(),
    },
    plans: plans.map((card, i) => mapPlanCard(card, i, totalBalance)),
    // Rollups across every plan the sponsor administers — the picker's summary strip.
    totals: {
      balance: totalBalance,
      employees: plans.reduce((sum, p) => sum + (p.employees || 0), 0),
      participants: plans.reduce((sum, p) => sum + (p.participants || 0), 0),
      planCount: plans.length,
    },
  };
}

function buildFundIndex(holdings, models) {
  const index = {};
  holdings.forEach((h, i) => {
    if (h.ticker) {
      // `pct` feeds the modal's no-detail fallback line; the sponsor feed carries no asset
      // class, so that part of the sentence is left out rather than printed as "undefined".
      index[h.ticker] = {
        ticker: h.ticker,
        name: h.name,
        pct: h.pct ?? null,
        assetClass: h.assetClass ?? null,
        expense: h.expenseRatio ?? null,
        color: colorAt(FUND_MIX, i),
      };
    }
  });
  models.forEach((m) => (m.rows || []).forEach((r) => {
    if (r.ticker && !index[r.ticker]) {
      index[r.ticker] = { ticker: r.ticker, name: r.name, expense: r.expenseRatio ?? null, color: m.color };
    }
  }));
  return index;
}

export function mapSponsorPlanResponse(api, today = new Date()) {
  if (!api) return null;
  const {
    plan, balance, payroll, ytdActivity, investments, workforce, loans, forfeitures, quickActions,
    care,
  } = api;

  const days = daysUntil(payroll?.nextDue, today);
  // Each lens states its own sum. The funds lens lists investments held outside models
  // (`investments.total`), the models lens the money inside them (`modelsTotal`); a
  // payload that states neither is one whose lists add up to the plan balance.
  const total = balance?.total ?? 0;
  const holdingsTotal = investments?.total ?? total;
  const modelsTotal = investments?.modelsTotal ?? total;
  // The label is derived from the EXACT balance/total ratio, not from the wire's `pct`:
  // that field is already rounded to two decimals, so the long tail of a large plan
  // arrives as a literal 0 and the "<0.01%" floor would never trigger.
  const holdings = (investments?.holdings || []).map((h) => ({
    ...h,
    pctLabel: formatPct(holdingsTotal ? (h.balance / holdingsTotal) * 100 : 0, { digits: 2, floor: 0.01 }),
  }));

  return {
    // Passed through as data. Any LABEL built from it belongs in a component: an adapter
    // has no `t`, so a string assembled here would be permanently English.
    plan: {
      ...plan,
      isSingleDivision: plan.divisionCount === 1,
    },
    balance: {
      ...balance,
      totalLabel: splitAmount(balance.total),
      sources: balance.sources.map((s) => ({ ...s, pctLabel: formatPct(s.pct) })),
      segments: balance.segments.map((s) => ({ ...s, pctLabel: formatPct(s.pct) })),
      // Two columns from four sources up — the canvas runs A Penny Saved's three in one
      // column and turns both Brinker (4) and TDA (6) into two.
      twoColumnSources: balance.sources.length >= 4,
    },
    // Absent means NO trustworthy payroll source for this plan (the warehouse carries
    // none), so the card is hidden rather than drawn from sample runs — the same rule
    // `ytdActivity` follows below. Normalised to null so the page has one thing to test.
    payroll: payroll ? {
      ...payroll,
      // The number, not the sentence — PayrollCard renders the countdown with `t`.
      daysUntilDue: days,
      isOverdue: days != null && days < 0,
    } : null,
    // Absent means the plan reports NO year-to-date activity — the card is hidden, not
    // drawn empty (INTEGRATION.md: absent != zero). Normalised to null so the page has
    // one thing to test; a payload that sends the block with an empty `categories` is
    // the same nothing on screen, so it collapses to null here too.
    //
    // `layout` rides along untouched: it is the canvas's own choice of pill row
    // ("inline" beside the title, "stacked" below it) and YtdActivityCard renders it.
    ytdActivity: (() => {
      // A $0 category is noise on a money-movement card, so it is dropped here (the card,
      // its modal and the modal's count all read the same list); all-zero collapses to null.
      const categories = (ytdActivity?.categories ?? []).filter((c) => Number(c.amount) !== 0);
      return categories.length ? { ...ytdActivity, categories } : null;
    })(),
    // Absent means no holdings source reconciles with the plan balance above, so the
    // whole card is hidden: a fund list that disagrees with the hero is worse than none.
    investments: investments ? {
      ...investments,
      // What every share and the models-lens summary are measured against.
      total,
      // What each lens's summary states: the listed holdings' sum, the listed models' sum.
      holdingsTotal,
      modelsTotal,
      holdings,
      // The disclosure split: the design shows six rows, then "+ N more holdings".
      topHoldings: holdings.slice(0, DISCLOSED_HOLDINGS),
      tailHoldings: holdings.slice(DISCLOSED_HOLDINGS),
      hasModels: (investments?.models || []).length > 0,
      models: (investments?.models || []).map((m) => ({ ...m, pctLabel: formatPct(m.pct) })),
      // Only offer the lens switcher when there is a second lens to switch to — a plan
      // whose every investment sits inside a model has nothing on the funds lens.
      lensSwitchable: (investments?.models || []).length > 0 && holdings.length > 0,
      // The fact-sheet overlay's inputs: structured detail per ticker (absent for a fund
      // nothing describes — the overlay then shows its PDF sheet only), and the `fund`
      // object per ticker from whichever lens lists it. A model row inherits its model's
      // accent; a plain holding takes a rank colour from the shared fund ramp.
      fundDetail: mapFundDetails(api.fundDetails),
      fundIndex: buildFundIndex(holdings, investments?.models || []),
    } : null,
    workforce: {
      ...workforce,
      // The funnel reads as a whole-number overview (`96% of employees`), so its three
      // shares round to the unit; the population bar below keeps its decimal, where the
      // difference between 18.7% and 19% is the point of the breakdown.
      eligiblePctLabel: formatPct(workforce.eligiblePct, { digits: 0 }),
      // Absent on a payload that predates the stat — the funnel drops the cell rather
      // than showing a zero the plan never reported.
      activeWithBalancePctLabel: workforce.activeWithBalance == null
        ? null
        : formatPct(workforce.activeWithBalancePct, { digits: 0 }),
      contributingPctLabel: formatPct(workforce.contributingPct, { digits: 0 }),
      segments: (workforce.segments || []).map((s) => ({
        ...s,
        pctLabel: formatPct(s.pct),
        // The bar prints its share inside the band, but only where the band is wide
        // enough to hold it — a 3.3% sliver would clip the text to an ellipsis.
        showBarLabel: (s.pct || 0) >= BAR_LABEL_MIN_PCT,
      })),
    },
    loans,
    // Absent means the plan holds none — the card is hidden, not zeroed.
    forfeitures: forfeitures || null,
    quickActions: quickActions || [],
    // The plan's relationship team + participant-services desk. No endpoint ships this
    // yet, so it is null on every plan today and CustomerCareCard falls back to the
    // design's sample contacts. Forwarded rather than defaulted here: when the backend
    // starts sending a `care` block, the real team appears with no frontend change.
    care: care || null,
  };
}

export const SPONSOR_DISCLOSED_HOLDINGS = DISCLOSED_HOLDINGS;
